import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, API_URL } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useAction, useApi } from '../lib/useApi';
import { ErrorBox, Loading, Notice, PageHeader, PasswordInput, StatusBadge, fmtDate, LoadFailed } from '../components/ui';

interface Settings {
  retention: { child_voice_retention_days: number; ai_processing_enabled: boolean; ai_training_opt_in: boolean; session_recording_retention_days: number };
  consents: Array<{ id: string; type: string; method: string; status: string; child_first_name: string | null; verified_at: string | null }>;
  childVoiceClips: { n: number; next_expiry: string | null };
  maxRetentionDays: number;
}

export function SafetyPage() {
  const { token } = useAuth();
  const { data, error, loading, reload } = useApi<Settings>('/safety/settings');
  const concerns = useApi<{ incidents: Array<{ id: string; category: string; severity: string; status: string; created_at: string }> }>('/safety/concerns');
  const act = useAction();
  const [form, setForm] = useState({ childVoiceRetentionDays: 30, aiProcessingEnabled: false, aiTrainingOptIn: false, sessionRecordingRetentionDays: 90 });

  useEffect(() => {
    if (data)
      setForm({
        childVoiceRetentionDays: data.retention.child_voice_retention_days,
        aiProcessingEnabled: data.retention.ai_processing_enabled,
        aiTrainingOptIn: data.retention.ai_training_opt_in,
        sessionRecordingRetentionDays: data.retention.session_recording_retention_days,
      });
  }, [data]);

  if (loading) return <Loading />;
  if (error || !data) return <LoadFailed error={error} onRetry={reload} />;

  const exportAll = async () => {
    await act.run(async () => {
      const res = await fetch(`${API_URL}/safety/export`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `iran-family-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
    }, 'Your export has downloaded.');
  };

  return (
    <>
      <PageHeader title="Safety & Privacy" termKey="page.safety" lead="You are in control. High-privacy settings are on by default for everyone under 18." />
      <Notice text={act.notice} />
      <ErrorBox error={act.error} />
      <div className="grid grid-2">
        <form
          className="card stack"
          onSubmit={async (e) => {
            e.preventDefault();
            await act.run(() => api('/safety/retention', { method: 'PUT', body: form }), 'Settings saved.');
            reload();
          }}
        >
          <h2>Recordings & AI</h2>
          <div className="field">
            <label htmlFor="ret">Keep children’s voice practice clips for</label>
            <div className="row">
              <input id="ret" type="number" min={1} max={data.maxRetentionDays} value={form.childVoiceRetentionDays} onChange={(e) => setForm({ ...form, childVoiceRetentionDays: Number(e.target.value) })} style={{ width: 100 }} />
              <span>days</span>
            </div>
            <span className="hint">
              Then they are permanently deleted. You have {data.childVoiceClips.n} clip(s); next deletion {fmtDate(data.childVoiceClips.next_expiry)}. Max {data.maxRetentionDays} days.
            </span>
          </div>
          <label className="check">
            <input type="checkbox" checked={form.aiProcessingEnabled} onChange={(e) => setForm({ ...form, aiProcessingEnabled: e.target.checked })} />
            <span>
              Use AI to tidy and translate family story transcripts <span className="small muted">(needs “AI help” consent; a family member reviews everything)</span>
            </span>
          </label>
          <label className="check">
            <input type="checkbox" checked={form.aiTrainingOptIn} onChange={(e) => setForm({ ...form, aiTrainingOptIn: e.target.checked })} />
            <span>
              Allow selected family story recordings to help improve Yoruba AI <span className="small muted">(off by default; needs its own consent; children’s practice clips are never used)</span>
            </span>
          </label>
          <div className="field">
            <label htmlFor="srr">Keep Live Circle recordings for (safeguarding)</label>
            <input id="srr" type="number" min={30} max={365} value={form.sessionRecordingRetentionDays} onChange={(e) => setForm({ ...form, sessionRecordingRetentionDays: Number(e.target.value) })} style={{ width: 100 }} />
          </div>
          <button className="btn" disabled={act.busy}>
            Save settings
          </button>
        </form>
        <section className="stack">
          <div className="card stack">
            <h2>Your data</h2>
            <p>Download everything Ìran holds about your family, in one file.</p>
            <button className="btn secondary" onClick={exportAll} disabled={act.busy}>
              ⬇ Export all data
            </button>
          </div>
          <EraseFamily />
          <div className="card stack">
            <h2>Consents</h2>
            <ul className="small">
              {data.consents.map((c) => (
                <li key={c.id}>
                  {c.type.replace(/_/g, ' ')} {c.child_first_name && <span className="yo">({c.child_first_name})</span>} · <StatusBadge status={c.status} />
                </li>
              ))}
            </ul>
            <Link to="/consent">Manage consents</Link>
          </div>
          <div className="card stack">
            <h2>Worried about something?</h2>
            <p>Anyone in your family — including children — can tell us. Serious concerns about a tutor pause them immediately.</p>
            <Link className="btn warm" to="/report">
              Report a concern
            </Link>
            {concerns.data && concerns.data.incidents.length > 0 && (
              <ul className="small">
                {concerns.data.incidents.map((i) => (
                  <li key={i.id}>
                    {i.category.replace('_', ' ')} · {fmtDate(i.created_at)} · <StatusBadge status={i.status} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </div>
    </>
  );
}

export function ReportPage() {
  const { user } = useAuth();
  const isChild = user?.role === 'child';
  const act = useAction();
  const [form, setForm] = useState({ category: isChild ? 'child_wellbeing' : 'tutor_conduct', severity: 'medium', description: '', sessionId: '' });
  const [done, setDone] = useState(false);
  if (done)
    return (
      <div className="card stack" role="status">
        <h1>Thank you for telling us 💛</h1>
        <p>{isChild ? 'You did the right thing. A grown-up from our safety team will look at this straight away. You can also tell a grown-up you trust.' : 'Our safeguarding team has been alerted.'}</p>
        <p className="small">If someone is in immediate danger, call your local emergency number (999 in the UK, 911 in the US, 112 in Nigeria).</p>
        <Notice text={act.notice} />
      </div>
    );
  return (
    <>
      <PageHeader title={isChild ? 'Tell us if something feels wrong' : 'Report a concern'} lead={isChild ? 'You will not be in trouble. Tell us what happened.' : 'Reports go straight to our safeguarding team.'} />
      <form
        className="card stack"
        onSubmit={async (e) => {
          e.preventDefault();
          const r = await act.run(() => api<{ autoAction: string | null }>('/safety/concerns', { body: { ...form, sessionId: form.sessionId || undefined } }));
          if (r) {
            if (r.autoAction === 'tutor_suspended') act.setNotice('The tutor involved has been paused while we look into this.');
            setDone(true);
          }
        }}
      >
        {!isChild && (
          <div className="row">
            <label className="field">
              <span className="label">What is it about?</span>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                <option value="tutor_conduct">A tutor’s behaviour</option>
                <option value="child_wellbeing">A child’s wellbeing</option>
                <option value="content">Something in a lesson or story</option>
                <option value="privacy">Privacy</option>
                <option value="technical">Something not working</option>
                <option value="other">Something else</option>
              </select>
            </label>
            <label className="field">
              <span className="label">How serious?</span>
              <select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Urgent — a child may be at risk</option>
              </select>
            </label>
          </div>
        )}
        <label className="field">
          <span className="label">{isChild ? 'What happened?' : 'Describe the concern'}</span>
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required minLength={1} />
        </label>
        {!isChild && (
          <label className="field">
            <span className="label">Live Circle session ID (optional)</span>
            <input value={form.sessionId} onChange={(e) => setForm({ ...form, sessionId: e.target.value })} placeholder="From your bookings" />
          </label>
        )}
        <ErrorBox error={act.error} />
        <button className="btn warm" disabled={act.busy}>
          Send to the safety team
        </button>
      </form>
    </>
  );
}

/**
 * Leaving, properly.
 *
 * Export sits above this on purpose: the last thing anyone should do before
 * erasing twenty years of a grandmother's voice is take a copy. The password is
 * asked for because the phone may have been handed to a child two minutes ago,
 * and the word must be typed because a mis-tap should never be able to do this.
 */
function EraseFamily() {
  const act = useAction();
  const { signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  return (
    <div className="card stack">
      <h2>Leave Ìran and erase everything</h2>
      <p className="muted">
        This deletes your family and everything in it: every recording and the audio itself, your stories, your children's profiles, the tree, the timeline.
        It cannot be undone and we cannot bring it back for you.
      </p>
      <p className="small muted">
        What we keep: the record that a consent was verified for a child, and when. Every name, including yours and your children's, is removed from it. We keep
        it because it is the safeguarding record, and it protects your child as much as it protects us.
      </p>
      {!open ? (
        <div>
          <button type="button" className="btn small ghost" onClick={() => setOpen(true)}>
            I want to erase everything
          </button>
        </div>
      ) : (
        <form
          className="stack"
          onSubmit={async (e) => {
            e.preventDefault();
            const r = await act.run(() => api<{ erased: boolean }>('/family/erase', { body: { password, confirm } }));
            if (r?.erased) signOut();
          }}
        >
          <p className="small">Export your data first if you have not. Once this finishes there is nothing left to export.</p>
          <label className="field">
            <span className="label">Your password</span>
            <PasswordInput value={password} onChange={setPassword} autoComplete="current-password" required />
          </label>
          <label className="field">
            <span className="label">Type ERASE to confirm</span>
            <input value={confirm} onChange={(e) => setConfirm(e.target.value.toUpperCase())} placeholder="ERASE" required />
          </label>
          <ErrorBox error={act.error} />
          <div className="row">
            <button className="btn danger small" disabled={act.busy || confirm !== 'ERASE' || !password}>
              {act.busy ? 'Erasing…' : 'Erase everything'}
            </button>
            <button type="button" className="btn small ghost" onClick={() => { setOpen(false); setPassword(''); setConfirm(''); act.setError(null); }}>
              Keep my family
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
